import { useMemo } from 'react';
import { useToast } from '@elva/ui';
import { extractApiErrorMessage } from '@/api/errors';
import { useDisconnectEmailAccount, useEmailAccounts } from '@/features/email/hooks/useEmailAccounts';
import { adaptEmailIntegrationAccounts } from '../adapters/emailIntegrationAdapters';
import { useEmailIntegration } from './useEmailIntegration';
import {
  mapEmailAccountsToIntegrationRows,
  useConnectEmailMailbox,
} from './useEmailIntegrationConnect';

export function useGeneralInfoEmailIntegration() {
  const { toast } = useToast();
  const { data: integration, isLoading: integrationLoading } = useEmailIntegration();
  const { data: accounts = [], isLoading: accountsLoading } = useEmailAccounts();
  const connect = useConnectEmailMailbox();
  const disconnect = useDisconnectEmailAccount();

  const rows = useMemo(() => {
    const fromSettings = adaptEmailIntegrationAccounts(integration);
    if (fromSettings.length > 0) return fromSettings;
    return mapEmailAccountsToIntegrationRows(accounts);
  }, [integration, accounts]);

  const isLoading = integrationLoading || (rows.length === 0 && accountsLoading);

  return {
    emailIntegration: {
      accounts: rows,
      isLoading,
      connecting: connect.isPending,
      disconnectingId: disconnect.isPending ? (disconnect.variables ?? null) : null,
      onConnectGmail: () => {
        void connect.mutateAsync().catch((err: unknown) => {
          toast({
            title: 'Connect failed',
            description: extractApiErrorMessage(err),
            variant: 'error',
          });
        });
      },
      onDisconnect: (accountId: string) => {
        void disconnect.mutateAsync(accountId).catch((err: unknown) => {
          toast({
            title: 'Disconnect failed',
            description: extractApiErrorMessage(err),
            variant: 'error',
          });
        });
      },
    },
  };
}
